import type { AxiosError } from 'axios'

import type { ErrorContext, ErrorHandlerPort, ErrorHandlerResult } from '../ports/ErrorHandlerPort'

/**
 * Registry that manages error handling strategies
 * Implements Strategy Pattern with Registry for dynamic error handler selection
 */
export class ErrorHandlerRegistry {
  private handlers: ErrorHandlerPort[] = []

  /**
   * Registers an error handler strategy
   * @param handler - The error handler to register
   */
  register(handler: ErrorHandlerPort): void {
    this.handlers.push(handler)
  }

  /**
   * Unregisters an error handler strategy
   * @param handler - The error handler to unregister
   */
  unregister(handler: ErrorHandlerPort): void {
    const index = this.handlers.indexOf(handler)
    if (index > -1) {
      this.handlers.splice(index, 1)
    }
  }

  /**
   * Handles an error by finding the appropriate strategy
   * @param error - The axios error object
   * @param context - Additional context about the request
   * @returns Promise<ErrorHandlerResult> - Result of error handling
   */
  async handleError(error: AxiosError, context: ErrorContext): Promise<ErrorHandlerResult> {
    // Find the first handler that can handle this error (in registration order)
    const handler = this.handlers.find(h => h.canHandle(error, context))

    if (handler) {
      return await handler.handleError(error, context)
    }

    // Default fallback if no handler can process the error
    return {
      handled: false,
      shouldShowToast: true,
      message: 'Unknown error occurred',
      shouldReject: true
    }
  }

  /**
   * Gets all registered handlers (for debugging/testing)
   */
  getHandlers(): readonly ErrorHandlerPort[] {
    return [...this.handlers]
  }

  /**
   * Clears all registered handlers
   */
  clear(): void {
    this.handlers = []
  }
}
